import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './upload-config-modal.html';
import type { UploadConfig } from '../services/api.types';
import { createUploadConfig, updateUploadConfig } from '../services/api.service';

const DEFAULT_CHECK_INTERVAL_MS = 5000;
const DEFAULT_STABILITY_MS = 75000;
const DEFAULT_TIMEOUT_MS = 600000;

@customElement({ name: 'upload-config-modal', template })
export class UploadConfigModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (config: UploadConfig) => void;
  @bindable public closed!: () => void;
  @bindable public itemToEdit: UploadConfig | null = null;

  public name = '';
  public selector = '';
  public checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS;
  public stabilityMs = DEFAULT_STABILITY_MS;
  public timeoutMs = DEFAULT_TIMEOUT_MS;
  public saving = false;
  public error = '';
  public selectorError = '';

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean {
    return this.itemToEdit !== null;
  }

  public attached(): void {
    if (this.itemToEdit) {
      this.name = this.itemToEdit.name;
      this.selector = this.itemToEdit.selector;
      this.checkIntervalMs = this.itemToEdit.checkIntervalMs;
      this.stabilityMs = this.itemToEdit.stabilityMs;
      this.timeoutMs = this.itemToEdit.timeoutMs;
    }

    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public close(): void {
    this.closed();
  }

  public validateSelector(): void {
    this.selectorError = '';
    if (!this.selector.trim()) return;
    try {
      new RegExp(this.selector);
    } catch {
      this.selectorError = this.i18n.tr('uploadConfigs.form.selectorInvalid');
    }
  }

  public async save(): Promise<void> {
    this.error = '';
    this.selectorError = '';

    if (!this.name.trim()) {
      this.error = this.i18n.tr('uploadConfigs.form.nameRequired');
      return;
    }
    if (!this.selector.trim()) {
      this.error = this.i18n.tr('uploadConfigs.form.selectorRequired');
      return;
    }

    try {
      new RegExp(this.selector);
    } catch {
      this.selectorError = this.i18n.tr('uploadConfigs.form.selectorInvalid');
      return;
    }

    this.saving = true;
    try {
      const dto = {
        name: this.name.trim(),
        selector: this.selector.trim(),
        checkIntervalMs: Number(this.checkIntervalMs),
        stabilityMs: Number(this.stabilityMs),
        timeoutMs: Number(this.timeoutMs),
      };

      const config = this.isEditMode && this.itemToEdit
        ? await updateUploadConfig(this.itemToEdit.id, dto)
        : await createUploadConfig(dto);

      this.saved(config);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
}
